import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { client, type RecordsFilters } from '../api/client';
import type { IngestStatus } from '../types';

export const STATS_QUERY_KEY = ['stats'];
export const LIVE_STATS_QUERY_KEY = ['live-stats'];
export const RECORDS_QUERY_KEY = 'records';
export const INGEST_STATUS_QUERY_KEY = 'ingest-status';

function isActiveStatus(status?: IngestStatus['status']) {
  return status === 'pending' || status === 'running';
}

export function useStats() {
  return useQuery({
    queryKey: STATS_QUERY_KEY,
    queryFn: client.getStats,
    staleTime: 60_000,
  });
}

export function useRecords(filters: RecordsFilters) {
  return useQuery({
    queryKey: [RECORDS_QUERY_KEY, filters],
    queryFn: () => client.getRecords(filters),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useIngestStatus(jobId: string | null, enabled = true) {
  return useQuery({
    queryKey: [INGEST_STATUS_QUERY_KEY, jobId],
    queryFn: () => client.getIngestStatus(jobId!),
    enabled: Boolean(jobId) && enabled,
    refetchInterval: (query) => {
      return isActiveStatus(query.state.data?.status) ? 2_000 : false;
    },
  });
}

export function useLiveStats() {
  return useQuery({
    queryKey: LIVE_STATS_QUERY_KEY,
    queryFn: client.getLiveStats,
    staleTime: 10_000,
    refetchInterval: (query) => {
      return isActiveStatus(query.state.data?.ingestStatus?.status) ? 5_000 : false;
    },
  });
}

export function useStartIngest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: client.startIngest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LIVE_STATS_QUERY_KEY });
    },
  });
}

export function usePauseIngest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: client.pauseIngest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INGEST_STATUS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: LIVE_STATS_QUERY_KEY });
    },
  });
}
